import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw, FileDown, Shield, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import { useNavigate } from 'react-router-dom';

export default function RegulatorRolePortal() {
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();

  async function load() {
    setLoading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        navigate('/auth');
        return;
      }

      const { data, error } = await supabase.rpc('regulator_company_overview', {
        p_user_id: user.id,
      });
      if (error) throw error;
      setCompanies(data || []);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function exportPdf() {
    setExporting(true);
    try {
      const { data, error } = await supabase.functions.invoke('export-regulator-overview-pdf');
      if (error) throw error;
      if (data?.url) window.open(data.url, '_blank');
      toast.success('Report exported successfully');
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setExporting(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="animate-spin h-8 w-8 text-muted-foreground" />
      </div>
    );
  }

  const avgTrustScore =
    companies.length > 0
      ? Math.round(companies.reduce((sum, c) => sum + (c.trust_score || 0), 0) / companies.length)
      : 0;

  const flaggedCompanies = companies.filter((c) => (c.flags || 0) > 0).length;

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold flex items-center gap-2 text-foreground">
            <Shield className="h-6 w-6 text-primary" />
            Regulator Portal
          </h2>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
            <Button size="sm" onClick={exportPdf} disabled={exporting}>
              {exporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileDown className="h-4 w-4 mr-1" />
              )}
              Export PDF
            </Button>
          </div>
        </div>

        {/* Statistics cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border-border bg-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Companies
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">{companies.length}</div>
            </CardContent>
          </Card>
          <Card className="border-border bg-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Avg Trust Score
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">{avgTrustScore}</div>
            </CardContent>
          </Card>
          <Card className="border-border bg-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Companies w/ Flags
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">{flaggedCompanies}</div>
            </CardContent>
          </Card>
        </div>

        {/* Aggregate chart */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-foreground">
              <BarChart3 className="h-5 w-5 text-primary" />
              Trust Scores Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={companies}>
                <XAxis dataKey="company_name" hide />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Bar dataKey="trust_score" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Company table */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-foreground">Company Overview</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {companies.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Jurisdiction</TableHead>
                    <TableHead className="text-right">Trust</TableHead>
                    <TableHead className="text-right">Proof %</TableHead>
                    <TableHead className="text-right">Risk</TableHead>
                    <TableHead className="text-right">Flags</TableHead>
                    <TableHead>Last Audit</TableHead>
                    <TableHead className="text-right">Detail</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {companies.map((c) => (
                    <TableRow key={c.company_id}>
                      <TableCell className="font-medium">{c.company_name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {c.jurisdiction || '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {c.trust_score ?? '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {c.proof_coverage ? `${c.proof_coverage}%` : '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {c.risk_score ?? '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{c.flags || 0}</TableCell>
                      <TableCell>
                        {c.last_audit_at ? new Date(c.last_audit_at).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`/audit/${c.company_id}`, '_blank')}
                        >
                          View Proofs
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                No companies assigned to your account.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
